import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  TextField,
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
} from '@mui/material';
import { Delete, Save } from '@mui/icons-material';
import { fetchGeneralInfo, updateGeneralInfo, deleteGeneralInfo, createGeneralInfo } from "../../actions/generalInfoActions";
import { uploadImage, clearMedia } from "../../actions/mediaActions";
import ImageContainerPreview from '../Common/ImageContainerPreview';

const GeneralSettings = ({ onActionComplete, newSettingName, onNewSettingCreated }) => {
  const dispatch = useDispatch();
  const generalInfo = useSelector(state => state.general_info?.general_info || []);
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState({});

  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(fetchGeneralInfo());
      } catch (error) {
        console.error('Error fetching general info:', error);
        onActionComplete(false, 'Error loading settings');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [dispatch]); // onActionComplete intentionally excluded — not memoized in parent

  useEffect(() => {
    if (generalInfo.length > 0) {
      const settingsMap = {};
      generalInfo.forEach(item => {
        settingsMap[item.general_info_key] = {
          value: item.general_info_value,
          notes: item.general_info_notes
        };
      });
      setSettings(settingsMap);
    }
  }, [generalInfo]);

  useEffect(() => {
    if (newSettingName) {
      const createNewSetting = async () => {
        try {
          await dispatch(createGeneralInfo({
            general_info_key: newSettingName,
            general_info_value: '',
            general_info_notes: ''
          }));
          onActionComplete(true, 'New setting created successfully');
          onNewSettingCreated();
        } catch (error) {
          onActionComplete(false, 'Error creating new setting');
        }
      };
      createNewSetting();
    }
  }, [newSettingName, dispatch]); // callbacks intentionally excluded — not memoized in parent

  const handleFieldChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value
      }
    }));
  };

  const handleNotesChange = (key, notes) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        notes
      }
    }));
  };

  const handleImageUpload = (e, key) => {
    dispatch(uploadImage(e.target.files[0]))
      .then((response) => {
        setSettings(prev => ({
          ...prev,
          [key]: {
            ...prev[key],
            value: response.data.image_url,
          }
        }));
        dispatch(clearMedia());
      });
  };

  const handleImageRemove = async (key) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value: ''
      }
    }));
  };

  const handleSave = async (key) => {
    try {
      await dispatch(updateGeneralInfo({
        general_info_key: key,
        general_info_value: settings[key].value,
        general_info_notes: settings[key].notes
      }));
      onActionComplete(true, 'Setting updated successfully');
    } catch (error) {
      onActionComplete(false, 'Error updating setting');
    }
  };

  const handleDelete = async (key) => {
    try {
      await dispatch(deleteGeneralInfo(key));
      onActionComplete(true, 'Setting deleted successfully');
    } catch (error) {
      onActionComplete(false, 'Error deleting setting');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!generalInfo.length) {
    return (
      <Typography variant="h6" color="textSecondary" align="center">
        No settings found
      </Typography>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {Object.entries(settings).map(([key, data]) => (
        <Card key={key} sx={{ mb: 2 }}>
          <CardActions sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}>
            <Typography variant="subtitle1" color="text.secondary">
              {key}
            </Typography>
            <Box display="flex" gap={1}>
              <Tooltip title="Save">
                <IconButton
                  color="primary"
                  onClick={() => handleSave(key)}
                  size="small"
                >
                  <Save />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  color="error"
                  onClick={() => handleDelete(key)}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Box>
          </CardActions>
          <CardContent sx={{ width: '100%', p: 2 }}>
            <Box display="flex" gap={2}>
              <Box flex={1}>
                {key.toLowerCase().includes('image') ? (
                  <ImageContainerPreview
                    title={key}
                    image={data.value}
                    name={key}
                    handleRemove={() => handleImageRemove(key)}
                    handleImageUpload={(e) => handleImageUpload(e, key)}
                    showCopyUrlButton={true}
                    sx={{
                      height: 200,
                      padding: 1,
                      background: '#FFFCF5',
                      objectFit: 'contain'
                    }}
                  />
                ) : (
                  <TextField
                    fullWidth
                    value={data.value}
                    onChange={(e) => handleFieldChange(key, e.target.value)}
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
              <Box flex={1}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={data.notes}
                  onChange={(e) => handleNotesChange(key, e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Add notes..."
                />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};

export default GeneralSettings; 